// react
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

// router
import { useNavigate, useParams } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner, ContextUser } from '../../../App.context';
import { ContextProject, ContextUserProjectPermissions } from '../Project/Project.context';
import { ContextSpendPlan } from '../ProjectV2/ProjectV2.context';

// components
import { PageDetailSkeleton } from '../../ui/PageDetailSkeleton/PageDetailSkeleton';
import { MonthlyGrid } from '../ProjectV2/SpendPlanning/MonthlyGrid';

// mui
import {
	Alert,
	AlertTitle,
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Paper,
	Stack,
	Tab,
	Tabs,
	TextField,
	Typography,
} from '@mui/material';

// icons
import {
	AccountBalance as IconAccount,
	Assignment as IconWorkPlan,
	CalendarMonth as IconCalendar,
	CheckCircle as IconComplete,
	Edit as IconEdit,
	History as IconHistory,
	OpenInNew as IconOpenInNew,
	Payments as IconSpendPlan,
	ReceiptLong as IconReceipt,
	Replay as IconReopen,
	Send as IconSend,
	ThumbUp as IconThumbUp,
	Undo as IconUndo,
} from '@mui/icons-material';

// utils
import { formatCurrency } from '../../../utils/currency';
import { formatDateRange } from '../../../utils/dates';
import { getProjectLocalCurrency, getProjectReportingCurrency } from '../../../utils/localCurrency';
import { resolveSpendPlanFxRate } from '../../../utils/fxRate';
import {
	canApprove as canApproveEntity,
	canComplete as canCompleteEntity,
	canEdit as canEditEntity,
	canReopen as canReopenEntity,
	canReturn as canReturnEntity,
	canSubmit as canSubmitEntity,
} from '../../../utils/makerChecker';

// services
import {
	AccountEarmarkSummary,
	fetchSpendPlan,
	getAccountEarmarks,
	updateSpendPlan,
} from '../../../services/api';

// types
import { Activity, SpendPlan, SpendPlanStatus } from '../../../@types';

// data
import { GRADIENTS } from '../../../data';

// design system
import { spacing } from '../../../utils/designSystem';

// sse
import { useV2StatusListener } from '../../../utils/sse/useV2StatusListener';

// v2 components
import { DialogAuditTimeline } from '../../dialogs/DialogAuditTimeline/DialogAuditTimeline';
import {
	getStatusColor,
	getStatusLabel,
	HeaderDetailPage,
} from '../../ui/HeaderDetailPage/HeaderDetailPage';
import { RowFinancialSummary } from '../../ui/RowFinancialSummary/RowFinancialSummary';
import { TruncatedText } from '../../ui/TruncatedText/TruncatedText';
import { SpendPlanTimeline } from '../../ui/SpendPlanTimeline/SpendPlanTimeline';
import { customFieldsToStats } from '../../ui/CustomFieldsFormSection/CustomFieldsFormSection';
import {
	VoucherPaymentReconciliation,
	convertVoucherAmount,
} from '../../ui/VoucherPaymentReconciliation/VoucherPaymentReconciliation';

// components
import { BtnSplit } from '../../ui/BtnSplit/BtnSplit';
import { OnChainStatusBadge } from '../../ui/OnChainStatusBadge/OnChainStatusBadge';
import { BlockchainProcessing } from '../../ui/BlockchainProcessing/BlockchainProcessing';

// Component

export const PageSpendPlan = () => {
	// hooks
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { spendPlanId } = useParams<{ spendPlanId: string }>();
	const spendPlanIdNumber = Number(spendPlanId);

	// context
	const { setBanner } = useContext(ContextBanner);
	const { user } = useContext(ContextUser);
	const { project } = useContext(ContextProject);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);
	const { accounts, vouchers, workPlans } = useContext(ContextSpendPlan);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [spendPlan, setSpendPlan] = useState<SpendPlan | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [, setIsUpdatingStatus] = useState(false);
	const [refreshKey, setRefreshKey] = useState(0);
	const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);
	const [rejectionReason, setRejectionReason] = useState('');
	const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
	const [rejectReason, setRejectReason] = useState('');
	const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
	const [reopenReason, setReopenReason] = useState('');
	const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
	const [approveDialogOpen, setApproveDialogOpen] = useState(false);
	// Approval locks/refunds funds on-chain; drives the in-dialog animation.
	const [isApproving, setIsApproving] = useState(false);
	const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
	const [auditTimelineOpen, setAuditTimelineOpen] = useState(false);
	const [auditActivities, setAuditActivities] = useState<Activity[]>([]);
	const [auditLoading, setAuditLoading] = useState(false);

	// Pending-review banner (shown while status === 'SUBMITTED')
	const [pendingReviewDismissed, setPendingReviewDismissed] = useState(false);
	const [accountEarmark, setAccountEarmark] = useState<AccountEarmarkSummary | null>(null);

	// Active detail tab: 'worksheet' (Monthly Worksheet) or 'vouchers'.
	const [activeTab, setActiveTab] = useState<'worksheet' | 'vouchers'>('worksheet');

	// theme

	// =========================================================================
	// Data fetching
	// =========================================================================

	const loadSpendPlan = useCallback(async () => {
		if (!spendPlanIdNumber || !accessToken || !idToken) {
			setIsLoading(false);
			return;
		}

		setIsLoading(true);
		try {
			const result = await fetchSpendPlan({ accessToken, idToken }, spendPlanIdNumber, {
				populate: 'account',
			});

			if (result.error) {
				setBanner({
					message: result.error.message || 'Failed to load spend plan',
					severity: 'error',
				});
				setSpendPlan(null);
			} else {
				setSpendPlan(result.data || null);
			}
		} catch (error) {
			setBanner({
				message: 'Failed to load spend plan',
				severity: 'error',
			});
			setSpendPlan(null);
		} finally {
			setIsLoading(false);
		}
	}, [spendPlanIdNumber, accessToken, idToken, setBanner]);

	/** Silently refresh spend plan data (e.g. header totals) without showing
	 *  skeleton / unmounting child components like MonthlyGrid. */
	const refreshSpendPlan = useCallback(async () => {
		if (!spendPlanIdNumber || !accessToken || !idToken) return;
		try {
			const result = await fetchSpendPlan({ accessToken, idToken }, spendPlanIdNumber, {
				populate: 'account',
			});
			if (!result.error && result.data) {
				setSpendPlan(result.data);
			}
		} catch {
			// Silent refresh — don't disturb the user for background updates
		}
	}, [spendPlanIdNumber, accessToken, idToken]);

	// Live updates: refetch this spend plan when someone else changes its status.
	useV2StatusListener({
		resource: 'spendplan',
		id: spendPlanIdNumber,
		onChange: refreshSpendPlan,
	});

	useEffect(() => {
		loadSpendPlan();
	}, [loadSpendPlan]);

	/** Re-run the blockchain spend-plan create flow. The backend splits the
	 *  compound write into two granular SDK calls (`account.createAccount`
	 *  then `envelope.createEnvelope`) and only retries whichever step
	 *  failed; pass `step` to target a specific leg from the per-step
	 *  retry buttons in the badge popover. Idempotent via the spend plan's
	 *  `referenceId`, safe to call even if the chain state has since been
	 *  reconciled. */
	const handleResyncBlockchain = useCallback(
		async (step?: 'account' | 'envelope' | 'lock') => {
			if (!spendPlan?.id || !accessToken || !idToken) return;
			try {
				const res = await fetch(
					`${process.env.REACT_APP_API_URL?.replace(/\/v1\/?$/, '')}/v2/spend-plans/${spendPlan.id}`,
					{
						method: 'PATCH',
						headers: {
							Authorization: `Bearer ${accessToken}`,
							User: String(idToken),
							'Content-Type': 'application/json',
						},
						body: JSON.stringify({ syncBlockchain: true, ...(step ? { step } : {}) }),
					}
				);
				if (!res.ok) {
					const err = await res.json().catch(() => ({}));
					setBanner({
						message: err?.error?.message || 'Failed to re-sync to blockchain.',
						severity: 'error',
					});
					return;
				}
				setBanner({
					message: step
						? `Re-synced ${
								step === 'account'
									? 'owner account'
									: step === 'envelope'
										? 'envelope'
										: 'funds lock'
							} on chain.`
						: 'Re-synced to blockchain.',
					severity: 'success',
				});
				await refreshSpendPlan();
			} catch (err: any) {
				setBanner({
					message: err?.message || 'Failed to re-sync to blockchain.',
					severity: 'error',
				});
			}
		},
		[spendPlan?.id, accessToken, idToken, setBanner, refreshSpendPlan]
	);

	const fetchAuditActivities = useCallback(async () => {
		if (!spendPlan?.id || !project?.id) return;

		setAuditLoading(true);
		try {
			const token = sessionStorage.getItem('accessToken');
			const id = sessionStorage.getItem('idToken');
			const res = await fetch(
				`${process.env.REACT_APP_API_URL}/activities?project=${project.id}&spendPlan=${spendPlan.id}&populate=actor&order=DESC&orderBy=created&limit=100&page=1`,
				{
					headers: {
						Authorization: `Bearer ${token}`,
						User: String(id),
					},
				}
			);

			if (res.ok) {
				const json = await res.json();
				setAuditActivities(json.data || []);
			}
		} catch (err) {
			console.error('Failed to fetch audit activities:', err);
		} finally {
			setAuditLoading(false);
		}
	}, [spendPlan?.id, project?.id]);

	useEffect(() => {
		fetchAuditActivities();
	}, [fetchAuditActivities]);

	// =========================================================================
	// Handlers
	// =========================================================================

	const handleEdit = useCallback(() => {
		if (!spendPlan) return;
		navigate(`/project/${project?.idWbg}/spend-plans/spend-planning/edit/${spendPlan.id}`);
	}, [navigate, project?.idWbg, spendPlan]);

	/** Generic status change helper */
	const performStatusChange = async (
		newStatus: SpendPlanStatus,
		extra?: Record<string, unknown>,
		successMessage?: string
	) => {
		if (!spendPlan || !accessToken || !idToken) return;
		setIsUpdatingStatus(true);
		try {
			const result = await updateSpendPlan({ accessToken, idToken }, spendPlan.id, {
				status: newStatus,
				...extra,
			});
			if (result.error) {
				setBanner({
					message: result.error.message || 'Failed to update status',
					severity: 'error',
				});
			} else {
				await loadSpendPlan();
				setRefreshKey((k) => k + 1);
				setBanner({
					message: successMessage || `Status changed to ${newStatus}`,
					severity: 'success',
				});
			}
			fetchAuditActivities();
		} catch {
			setBanner({ message: 'Failed to update status', severity: 'error' });
		} finally {
			setIsUpdatingStatus(false);
		}
	};

	const handleSubmitForApproval = async () => {
		setSubmitDialogOpen(false);
		await performStatusChange('SUBMITTED', undefined, 'Spend plan submitted for approval');
	};

	const handleApprove = async () => {
		setIsApproving(true);
		await performStatusChange('APPROVED', undefined, 'Spend plan approved');
		setIsApproving(false);
		setApproveDialogOpen(false);
	};

	const handleRejectConfirm = async () => {
		setRejectDialogOpen(false);
		await performStatusChange(
			'DRAFT',
			{ rejectionReason: rejectReason.trim() || undefined },
			'Spend plan rejected'
		);
		setRejectReason('');
	};

	const handleReturnToDraftConfirm = async () => {
		setRejectionDialogOpen(false);
		await performStatusChange(
			'DRAFT',
			{ rejectionReason: rejectionReason.trim() || undefined },
			'Spend plan returned to draft'
		);
		setRejectionReason('');
	};

	const handleComplete = async () => {
		setCompleteDialogOpen(false);
		await performStatusChange('COMPLETED', undefined, 'Spend plan marked as completed');
	};

	const handleReopenConfirm = async () => {
		setReopenDialogOpen(false);
		await performStatusChange(
			'DRAFT',
			{ reopenReason: reopenReason.trim() },
			'Spend plan reopened'
		);
		setReopenReason('');
	};

	// =========================================================================
	// Derived values
	// =========================================================================

	const account = useMemo(() => {
		if (!spendPlan) return null;
		if (typeof spendPlan.account === 'number') {
			return accounts.find((a) => a.id === spendPlan.account) || null;
		}
		return spendPlan.account || null;
	}, [spendPlan, accounts]);

	const accountName = useMemo(() => {
		if (!account) return '----';
		if (typeof account.name === 'string') return account.name;
		return `Account #${account.id}`;
	}, [account]);

	// =========================================================================
	// Pending-review earmark fetch (SUBMITTED only)
	// =========================================================================

	useEffect(() => {
		if (
			!spendPlan ||
			spendPlan.status !== 'SUBMITTED' ||
			!account?.id ||
			!accessToken ||
			!idToken
		) {
			setAccountEarmark(null);
			return;
		}
		let cancelled = false;
		(async () => {
			try {
				const res = await getAccountEarmarks({ accessToken, idToken }, { account: account.id });
				if (cancelled) return;
				const summary = Array.isArray(res.data) ? res.data[0] || null : null;
				setAccountEarmark(summary);
			} catch {
				if (!cancelled) setAccountEarmark(null);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [spendPlan, account?.id, accessToken, idToken, refreshKey]);

	// Reset the dismiss when the plan id changes or leaves SUBMITTED.
	useEffect(() => {
		setPendingReviewDismissed(false);
	}, [spendPlanIdNumber, spendPlan?.status]);

	// =========================================================================
	// Vouchers tied to this spend plan
	// =========================================================================

	/** All non-cancelled vouchers tied to this spend plan. */
	const spendPlanVouchers = useMemo(() => {
		if (!spendPlanIdNumber) return [];
		return (vouchers || []).filter(
			(v) => v.spendPlanId === spendPlanIdNumber && v.status !== 'CANCELLED'
		);
	}, [vouchers, spendPlanIdNumber]);

	/** Sum vouchers (FX-converted into spend plan currency) by status bucket. */
	const voucherTotals = useMemo(() => {
		const planCurrency = spendPlan?.currency || 'USD';
		let committed = 0; // GENERATED / READY / VERIFIED
		let spent = 0; // REDEEMED
		let missing = 0; // vouchers we couldn't convert

		for (const v of spendPlanVouchers) {
			const amount = Number(v.amount || 0);
			const converted = convertVoucherAmount(v, amount, planCurrency);
			if (converted == null) {
				missing += 1;
				continue;
			}
			if (v.status === 'REDEEMED') {
				spent += converted;
			} else {
				// GENERATED, READY, VERIFIED — money is allocated but not yet disbursed
				committed += converted;
			}
		}
		return { committed, spent, missing };
	}, [spendPlanVouchers, spendPlan?.currency]);

	// =========================================================================
	// CTA buttons (customAction for HeaderDetailPage)
	// =========================================================================

	const ctaActions = useMemo(() => {
		if (isLoading || !spendPlan) return undefined;

		const makerUser = auditActivities.find((a) => a.type === 'SPEND_PLAN_CREATED')?.actor;
		const makerUserId = (typeof makerUser === 'object' ? makerUser?.id : makerUser) ?? null;
		const gateInput = {
			status: spendPlan.status,
			permissions: userProjectPermissions,
			resource: 'spendPlan' as const,
			makerUserId,
			currentUserId: user?.id ?? null,
		};

		const actions: {
			children: string;
			onClick: () => void;
			startIcon?: React.ReactNode;
		}[] = [];

		if (canSubmitEntity(gateInput)) {
			actions.push({
				children: 'Submit for Approval',
				onClick: () => setSubmitDialogOpen(true),
				startIcon: <IconSend />,
			});
		}
		if (canEditEntity(gateInput)) {
			actions.push({
				children: 'Edit',
				onClick: handleEdit,
				startIcon: <IconEdit />,
			});
		}
		if (canApproveEntity(gateInput)) {
			actions.push({
				children: 'Approve',
				onClick: () => setApproveDialogOpen(true),
				startIcon: <IconThumbUp />,
			});
		}
		if (canReturnEntity(gateInput)) {
			actions.push({
				children: 'Reject',
				onClick: () => setRejectDialogOpen(true),
				startIcon: <IconUndo />,
			});
		}
		if (canCompleteEntity(gateInput)) {
			actions.push({
				children: 'Mark Completed',
				onClick: () => setCompleteDialogOpen(true),
				startIcon: <IconComplete />,
			});
		}
		if (canReopenEntity(gateInput, { fromApproved: true })) {
			actions.push({
				children: 'Reopen for Changes',
				onClick: () => setReopenDialogOpen(true),
				startIcon: <IconReopen />,
			});
		}

		// Always add audit log option
		actions.push({
			children: 'View Audit Log',
			onClick: () => setAuditTimelineOpen(true),
			startIcon: <IconHistory />,
		});

		if (actions.length === 0) return undefined;
		return <BtnSplit actions={actions} />;
	}, [isLoading, spendPlan, handleEdit, userProjectPermissions, user?.id, auditActivities]);

	// =========================================================================
	// Audit timeline entries
	// =========================================================================

	// =========================================================================
	// Not found
	// =========================================================================

	if (!isLoading && !spendPlan) {
		return (
			<Box sx={{ px: 3, py: 4 }}>
				<Typography>Spend Plan not found</Typography>
			</Box>
		);
	}

	const currency = spendPlan?.currency || 'USD';

	// Local-currency restatement basis. A spend plan is denominated in its
	// account currency; its linked work plan carries the budget rate
	// (amountLocal / amount) used to restate every figure into the project's
	// local currency on a single, FX-variance-free basis.
	const spendPlanWorkPlan = spendPlan
		? workPlans.find((wp) => {
				const workPlanId =
					typeof spendPlan.workPlan === 'number' ? spendPlan.workPlan : spendPlan.workPlan?.id;
				return wp.id === workPlanId;
			})
		: undefined;
	const spendPlanLocalCurrency =
		getProjectLocalCurrency(project) || spendPlanWorkPlan?.currencyLocal;
	const spendPlanFx = resolveSpendPlanFxRate(spendPlan, spendPlanWorkPlan, project);
	const spendPlanBudgetRate = spendPlanFx?.rate ?? null;
	// Suppress the local restatement when the figure is already in local
	// currency (the equivalent would just duplicate it).
	const showSpendPlanLocal =
		spendPlanBudgetRate != null && !!spendPlanLocalCurrency && currency !== spendPlanLocalCurrency;

	// Header stats — Account, Timeline, Total Planned
	const spStatusColors = spendPlan ? getStatusColor(spendPlan.status) : null;

	const headerStats = spendPlan
		? [
				// Status
				{
					label: 'Status',
					value: (
						<Chip
							label={getStatusLabel(spendPlan.status, t)}
							size="small"
							sx={{
								bgcolor: spStatusColors?.bg,
								color: spStatusColors?.text,
								fontSize: { xs: '1rem', sm: '1.125rem' },
								fontWeight: 600,
								height: 28,
								px: 1,
							}}
						/>
					),
				},
				{
					label: 'Account',
					value: (
						<Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
							<IconAccount sx={{ color: GRADIENTS.activity.icon, fontSize: 18, flexShrink: 0 }} />
							<TruncatedText text={accountName} component="span" sx={{ maxWidth: 200 }} />
							{account?.id != null && (
								<IconOpenInNew
									sx={{ color: GRADIENTS.activity.subtitle, fontSize: 14, flexShrink: 0 }}
								/>
							)}
						</Stack>
					),
					...(account?.id != null
						? { to: `/project/${project?.idWbg}/account/${account.id}` }
						: {}),
				},
				{
					label: 'Account Currency',
					value: <span>{currency}</span>,
				},
				...(spendPlanWorkPlan
					? [
							{
								label: 'Work Plan',
								value: (
									<Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
										<IconWorkPlan
											sx={{ color: GRADIENTS.workPlan.icon, fontSize: 18, flexShrink: 0 }}
										/>
										<TruncatedText
											text={spendPlanWorkPlan.name}
											component="span"
											sx={{ maxWidth: 200 }}
										/>
										<IconOpenInNew
											sx={{ color: GRADIENTS.workPlan.subtitle, fontSize: 14, flexShrink: 0 }}
										/>
									</Stack>
								),
								to: `/project/${project?.idWbg}/spend-plans/work-planning/work-plan/${spendPlanWorkPlan.id}`,
							},
						]
					: []),
				{
					label: 'Timeline',
					value: (
						<Stack direction="row" alignItems="center" spacing={0.75}>
							<IconCalendar sx={{ color: GRADIENTS.activity.icon, fontSize: 18 }} />
							<span>
								{formatDateRange({
									dateStart: spendPlan.dateStart,
									dateEnd: spendPlan.dateEnd,
									t,
								}) ?? t('noTimelineSet')}
							</span>
						</Stack>
					),
				},
				// On-chain status — always shown so reviewers know whether the
				// chain commitment exists. Three steps in the popover:
				//   1. Owner account     — minted at spend-plan creation
				//   2. Spend-plan envelope — created at spend-plan creation
				//   3. Funds locked      — runs on SUBMITTED → APPROVED
				// While the plan is still in DRAFT the pill renders neutral
				// ("Awaiting approval") because nothing has been written to
				// chain yet from the user's perspective; the popover lists
				// each step with its own gating reason. Once the chain
				// writes start landing we surface the per-step breakdown so
				// QA can tell apart "envelope never created" (legacy plans
				// that predate the integration) from "envelope exists,
				// worksheet sync failed", etc. Click the pill for the full
				// breakdown.
				{
					label: 'On-chain',
					value: (
						<OnChainStatusBadge
							notApplicable={
								spendPlan.status === 'DRAFT'
									? 'The on-chain envelope is created when this spend plan is approved. No blockchain activity has happened yet.'
									: false
							}
							notApplicableLabel="Awaiting approval"
							steps={[
								{
									key: 'account',
									label: 'Owner account on chain',
									description:
										'Account record minted on chain for the spend-plan owner. One per owner — reused across plans.',
									status:
										spendPlan.status === 'DRAFT'
											? 'notApplicable'
											: spendPlan.accountTransactionHash
												? 'success'
												: spendPlan.blockchainAccountId != null
													? 'pending'
													: 'failed',
									tooltip:
										spendPlan.status === 'DRAFT'
											? 'Created when the spend plan is approved.'
											: spendPlan.accountTransactionHash
												? `Account #${spendPlan.blockchainAccountId}`
												: spendPlan.blockchainAccountId != null
													? `Account #${spendPlan.blockchainAccountId} is referenced, but no creation transaction was found on chain.`
													: 'No account record found on chain for this owner.',
									txHash: spendPlan.accountTransactionHash || undefined,
									errorMessage:
										spendPlan.status !== 'DRAFT' &&
										!spendPlan.accountTransactionHash &&
										spendPlan.blockchainAccountId == null
											? 'The on-chain account was never created. This usually means the spend plan was created before the blockchain integration was wired up — re-sync to create it now.'
											: undefined,
									onRetry:
										spendPlan.status !== 'DRAFT' && !spendPlan.accountTransactionHash
											? () => handleResyncBlockchain('account')
											: undefined,
								},
								{
									key: 'envelope',
									label: 'Spend-plan envelope',
									description:
										'On-chain envelope holding this spend plan’s commitment. Required before any monthly cycle, voucher mint, or payment can settle.',
									status:
										spendPlan.status === 'DRAFT'
											? 'notApplicable'
											: spendPlan.envelopeTransactionHash
												? 'success'
												: spendPlan.envelopeId != null
													? 'pending'
													: spendPlan.blockchainAccountId != null
														? 'failed'
														: 'notApplicable',
									tooltip:
										spendPlan.status === 'DRAFT'
											? 'Created when the spend plan is approved.'
											: spendPlan.envelopeTransactionHash
												? `Envelope #${spendPlan.envelopeId}`
												: spendPlan.envelopeId != null
													? `Envelope #${spendPlan.envelopeId} is referenced, but no creation transaction was found on chain.`
													: spendPlan.blockchainAccountId != null
														? 'Envelope was not created. Re-sync to retry.'
														: 'Waiting on the owner account.',
									txHash: spendPlan.envelopeTransactionHash || undefined,
									errorMessage:
										spendPlan.status !== 'DRAFT' &&
										!spendPlan.envelopeTransactionHash &&
										spendPlan.envelopeId == null &&
										spendPlan.blockchainAccountId != null
											? 'Envelope creation never completed on chain.'
											: undefined,
									onRetry:
										spendPlan.status !== 'DRAFT' &&
										!spendPlan.envelopeTransactionHash &&
										spendPlan.blockchainAccountId != null
											? () => handleResyncBlockchain('envelope')
											: undefined,
								},
								// Step 3: token lock on the envelope. Fires automatically
								// when the PIU approves the plan (SUBMITTED → APPROVED) —
								// the backend blocks the status change if `syncToBlockchain`
								// fails. The lock's on-chain tx hash is resolved from the
								// blk.* ledger, so this step only reads green once an actual
								// LOCK transaction is found; a plan that's past SUBMITTED but
								// has no lock hash shows as pending rather than success.
								{
									key: 'lock',
									label: 'Funds locked on chain',
									description:
										'Worksheet total locked against the envelope. Fires automatically when the spend plan is approved, and re-locks on every monthly worksheet sync.',
									status:
										spendPlan.status === 'DRAFT' || spendPlan.status === 'SUBMITTED'
											? 'notApplicable'
											: spendPlan.lockTransactionHash
												? 'success'
												: spendPlan.envelopeId != null
													? 'pending'
													: 'notApplicable',
									tooltip:
										spendPlan.status === 'DRAFT'
											? 'Locked when the spend plan is approved.'
											: spendPlan.status === 'SUBMITTED'
												? 'Will lock automatically on approval.'
												: spendPlan.lockTransactionHash
													? `Worksheet total of ${formatCurrency({ value: spendPlan.totalPlanned, currency })} locked against envelope #${spendPlan.envelopeId}.`
													: spendPlan.envelopeId != null
														? 'No funds-lock transaction was found on chain yet.'
														: 'Waiting on the envelope.',
									txHash: spendPlan.lockTransactionHash || undefined,
									onRetry:
										spendPlan.status !== 'DRAFT' &&
										spendPlan.status !== 'SUBMITTED' &&
										!spendPlan.lockTransactionHash &&
										spendPlan.envelopeId != null
											? () => handleResyncBlockchain('lock')
											: undefined,
								},
							]}
							onRetry={() => handleResyncBlockchain()}
							compact
						/>
					),
				},
				...customFieldsToStats(
					(project?.customFieldDefinitions || []).find((d) => d.entityType === 'spend_plan')
						?.definitions || [],
					spendPlan.customData || {}
				),
			]
		: [];

	return (
		<Box>
			{/* Header */}
			<HeaderDetailPage
				loading={isLoading}
				loadingActions={1}
				icon={IconSpendPlan}
				breadcrumb={{
					label: t('spendPlan.spendPlanning'),
					to: `/project/${project?.idWbg}/spend-plans/spend-planning`,
				}}
				title={
					typeof spendPlan?.name === 'string'
						? spendPlan.name
						: `Spend Plan #${spendPlan?.id || ''}`
				}
				variant="financial"
				stats={headerStats}
				customAction={ctaActions}
			/>

			{/* Pending-review banner: shown while SUBMITTED so reviewers can see the
			    account impact that auto-syncs to chain on approval. Reads as a
			    left-to-right deduction (balance − prior earmarks − this plan =
			    available) so the columns reconcile visually. The "Existing
			    earmarks" column is only rendered when nonzero — most plans
			    don't have prior APPROVED siblings and the extra column adds
			    noise. */}
			{spendPlan?.status === 'SUBMITTED' && !pendingReviewDismissed && (
				<Box sx={{ px: { xs: spacing.sm, sm: spacing.md }, pt: spacing.md }}>
					<Alert severity="info" variant="outlined" onClose={() => setPendingReviewDismissed(true)}>
						<AlertTitle>Awaiting approval</AlertTitle>
						{(() => {
							const planTotal = Number(spendPlan.totalPlanned || 0);
							const balance = accountEarmark?.balance ?? null;
							const finalized = accountEarmark?.finalizedEarmarked ?? 0;
							const projected = balance !== null ? balance - finalized - planTotal : null;
							const fmt = (n: number) => formatCurrency({ value: n, currency });
							return (
								<Stack
									direction={{ xs: 'column', sm: 'row' }}
									spacing={{ xs: 0.5, sm: 3 }}
									sx={{ mt: 0.5 }}>
									<Box>
										<Typography variant="caption" color="text.secondary">
											Account balance
										</Typography>
										<Typography variant="body2" fontWeight={600}>
											{balance !== null ? fmt(balance) : '—'}
										</Typography>
									</Box>
									{finalized > 0 && (
										<Box>
											<Typography variant="caption" color="text.secondary">
												Existing earmarks
											</Typography>
											<Typography variant="body2" fontWeight={600}>
												−{fmt(finalized)}
											</Typography>
										</Box>
									)}
									<Box>
										<Typography variant="caption" color="text.secondary">
											This plan
										</Typography>
										<Typography variant="body2" fontWeight={600}>
											−{fmt(planTotal)}
										</Typography>
									</Box>
									<Box>
										<Typography variant="caption" color="text.secondary">
											Available after approval
										</Typography>
										<Typography
											variant="body2"
											fontWeight={600}
											color={projected !== null && projected < 0 ? 'error.main' : 'text.primary'}>
											{projected !== null ? fmt(projected) : '—'}
										</Typography>
									</Box>
								</Stack>
							);
						})()}
					</Alert>
				</Box>
			)}

			{/* Financial Summary Row — same component used across the app for
			    consistent presentation. For a spend plan: planned = totalPlanned,
			    committed = pending vouchers, spent = redeemed vouchers (all
			    FX-converted into the spend plan's account currency). */}
			{!isLoading && spendPlan && (
				<Box
					sx={{
						borderBottom: '1px solid',
						borderColor: 'divider',
						// HeaderDetailPage already renders a bottom border, and
						// RowFinancialSummary renders its own top border — suppress
						// the inner one to avoid a doubled-up divider.
						'& > div': { borderTop: 'none' },
					}}>
					<RowFinancialSummary
						variant="workPlan"
						data={{
							currency: spendPlan.currency || currency,
							planned: Number(spendPlan.totalPlanned || 0),
							committed: voucherTotals.committed,
							spent: voucherTotals.spent,
							currencyLocal: showSpendPlanLocal ? spendPlanLocalCurrency : null,
							fx: showSpendPlanLocal ? spendPlanFx : null,
						}}
						compact
					/>
				</Box>
			)}

			{/* Spend Plan Lifecycle Stepper */}
			{!isLoading && spendPlan && (
				<Box sx={{ px: { xs: spacing.sm, sm: spacing.md }, pt: spacing.md }}>
					<SpendPlanTimeline status={spendPlan.status} activities={auditActivities} />
				</Box>
			)}

			{/* Content — classic underline tabs: Monthly Worksheet & Vouchers */}
			{!isLoading && spendPlan && (
				<Box
					sx={{
						px: { xs: spacing.sm, sm: spacing.md },
						pt: spacing.md,
						borderBottom: '1px solid',
						borderColor: 'divider',
					}}>
					<Tabs
						value={activeTab}
						onChange={(_, v) => setActiveTab(v)}
						textColor="primary"
						indicatorColor="primary">
						<Tab
							label="Monthly Worksheet"
							value="worksheet"
							sx={{ textTransform: 'none', fontWeight: 600 }}
						/>
						<Tab
							label={
								spendPlanVouchers.length > 0 ? `Vouchers (${spendPlanVouchers.length})` : 'Vouchers'
							}
							value="vouchers"
							sx={{ textTransform: 'none', fontWeight: 600 }}
						/>
					</Tabs>
				</Box>
			)}

			<Box sx={{ px: { xs: spacing.sm, sm: spacing.md }, py: spacing.md }}>
				{isLoading ? (
					<PageDetailSkeleton />
				) : spendPlan ? (
					activeTab === 'worksheet' ? (
						<MonthlyGrid
							key={refreshKey}
							spendPlanId={spendPlanIdNumber}
							spendPlanStatus={spendPlan.status}
							accounts={accounts}
							onSaveComplete={refreshSpendPlan}
							planCurrency={currency}
							reportingCurrency={getProjectReportingCurrency(project)}
							localPerReportingRate={spendPlanBudgetRate}
						/>
					) : spendPlanVouchers.length > 0 ? (
						<VoucherPaymentReconciliation
							vouchers={spendPlanVouchers}
							reportingCurrency={spendPlan.currency || currency}
							title={null}
							reportingChipLabel={`Plan ccy: ${spendPlan.currency || currency}`}
							alwaysShow
							onVoucherClick={(v) =>
								navigate(`/project/${project?.idWbg}/spend-plans/voucher/${v.id}`)
							}
						/>
					) : (
						<Paper elevation={0} variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
							<IconReceipt
								sx={{
									fontSize: 40,
									color: 'text.disabled',
									mb: 1,
								}}
							/>
							<Typography variant="body1" sx={{ fontWeight: 600 }}>
								No vouchers yet
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Vouchers drawn from this spend plan will appear here.
							</Typography>
						</Paper>
					)
				) : null}
			</Box>

			{/* Submit for Approval Confirmation */}
			<Dialog
				open={submitDialogOpen}
				onClose={() => setSubmitDialogOpen(false)}
				maxWidth="xs"
				fullWidth>
				<DialogTitle>Submit for Approval</DialogTitle>
				<DialogContent>
					<Typography variant="body2" color="text.secondary">
						Are you sure you want to submit this spend plan for approval?
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setSubmitDialogOpen(false)}>Cancel</Button>
					<Button onClick={handleSubmitForApproval} variant="contained" color="primary">
						Submit
					</Button>
				</DialogActions>
			</Dialog>

			{/* Approve Confirmation */}
			<Dialog
				open={approveDialogOpen}
				onClose={isApproving ? undefined : () => setApproveDialogOpen(false)}
				maxWidth="xs"
				fullWidth>
				<DialogTitle>Approve Spend Plan</DialogTitle>
				<DialogContent>
					{isApproving ? (
						<BlockchainProcessing status="LOADING" />
					) : (
						<Typography variant="body2" color="text.secondary">
							Are you sure you want to approve this spend plan?
						</Typography>
					)}
				</DialogContent>
				{!isApproving && (
					<DialogActions>
						<Button onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
						<Button onClick={handleApprove} variant="contained" color="success">
							Approve
						</Button>
					</DialogActions>
				)}
			</Dialog>

			{/* Rejection Reason Dialog (Return to Draft from APPROVED) */}
			<Dialog
				open={rejectionDialogOpen}
				onClose={() => setRejectionDialogOpen(false)}
				maxWidth="sm"
				fullWidth>
				<DialogTitle>Return Spend Plan to Draft</DialogTitle>
				<DialogContent>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
						Provide a reason for returning this spend plan to draft status.
					</Typography>
					<TextField
						autoFocus
						fullWidth
						multiline
						rows={3}
						label="Reason"
						value={rejectionReason}
						onChange={(e) => setRejectionReason(e.target.value)}
						placeholder="e.g. Amounts need revision, missing allocations..."
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setRejectionDialogOpen(false)}>Cancel</Button>
					<Button
						onClick={handleReturnToDraftConfirm}
						variant="contained"
						color="warning"
						disabled={!rejectionReason.trim()}>
						Return to Draft
					</Button>
				</DialogActions>
			</Dialog>

			{/* Reject Dialog (formal rejection from SUBMITTED) */}
			<Dialog
				open={rejectDialogOpen}
				onClose={() => setRejectDialogOpen(false)}
				maxWidth="sm"
				fullWidth>
				<DialogTitle>Reject Spend Plan</DialogTitle>
				<DialogContent>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
						Provide a reason for rejecting this spend plan. The submitter will be able to edit and
						resubmit it.
					</Typography>
					<TextField
						autoFocus
						fullWidth
						multiline
						rows={3}
						label="Rejection Reason"
						value={rejectReason}
						onChange={(e) => setRejectReason(e.target.value)}
						placeholder="e.g. Budget amounts do not align with approved work plan..."
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
					<Button
						onClick={handleRejectConfirm}
						variant="contained"
						color="error"
						disabled={!rejectReason.trim()}>
						Reject
					</Button>
				</DialogActions>
			</Dialog>

			{/* Complete Confirmation */}
			<Dialog
				open={completeDialogOpen}
				onClose={() => setCompleteDialogOpen(false)}
				maxWidth="xs"
				fullWidth>
				<DialogTitle>Mark as Completed</DialogTitle>
				<DialogContent>
					<Typography variant="body2" color="text.secondary">
						Are you sure you want to mark this spend plan as completed?
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setCompleteDialogOpen(false)}>Cancel</Button>
					<Button onClick={handleComplete} variant="contained" color="primary">
						Mark Completed
					</Button>
				</DialogActions>
			</Dialog>

			{/* Reopen Dialog */}
			<Dialog
				open={reopenDialogOpen}
				onClose={() => setReopenDialogOpen(false)}
				maxWidth="sm"
				fullWidth>
				<DialogTitle>Reopen Spend Plan for Changes</DialogTitle>
				<DialogContent>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
						This will return the spend plan to draft status so it can be edited. Provide a reason
						for reopening.
					</Typography>
					<TextField
						autoFocus
						fullWidth
						multiline
						rows={3}
						label="Reason"
						value={reopenReason}
						onChange={(e) => setReopenReason(e.target.value)}
						placeholder="e.g. Additional allocations needed, corrections required..."
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setReopenDialogOpen(false)}>Cancel</Button>
					<Button
						onClick={handleReopenConfirm}
						variant="contained"
						color="warning"
						disabled={!reopenReason.trim()}>
						Reopen
					</Button>
				</DialogActions>
			</Dialog>

			{/* Audit Log Dialog */}
			<DialogAuditTimeline
				isOpen={auditTimelineOpen}
				onClose={() => setAuditTimelineOpen(false)}
				title="Spend Plan Audit Log"
				activities={auditActivities}
				isLoading={auditLoading}
			/>
		</Box>
	);
};
